class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Updated cart update function that properly refreshes sections
function updateCart(lineKey, quantity) {
  const body = JSON.stringify({
    id: lineKey,
    quantity: quantity,
    sections: 'cart-drawer,cart-icon-bubble'
  });

  fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: body
  })
    .then(response => response.json())
    .then(state => {
      // Update cart icon bubble
      updateCartIcon(state);
      
      // Refresh the cart drawer content
      refreshCartDrawer();


      
      // If cart is empty, update the drawer state
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.classList.toggle('is-empty', state.item_count === 0);
      }
    })
    .catch(error => {
      console.error('Cart update failed:', error);
    });
}

// Function to refresh cart drawer content
function refreshCartDrawer() {
  fetch('/cart?section_id=cart-drawer')
    .then(response => response.text())
    .then(html => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newCartDrawer = doc.querySelector('.drawer__inner');
      const currentCartDrawer = document.querySelector('.drawer__inner');
      
      if (newCartDrawer && currentCartDrawer) {
        currentCartDrawer.innerHTML = newCartDrawer.innerHTML;
      }
    })
    .catch(error => {
      console.error('Failed to refresh cart drawer:', error);
    });
}

// Function to update cart icon
function updateCartIcon(cartState) {
  const cartIconBubble = document.querySelector('#cart-icon-bubble');
  if (cartIconBubble) {
    const cartCount = cartIconBubble.querySelector('.cart-count-bubble span');
    if (cartCount) {
      cartCount.textContent = cartState.item_count;
      cartIconBubble.style.display = cartState.item_count > 0 ? 'block' : 'none';
    }
  }
}

// Updated event delegation for cart drawer interactions
document.addEventListener('click', (e) => {
  // 🗑 Remove item (trash bin)
  const removeBtn = e.target.closest('[data-cart-remove]');
  if (removeBtn) {
    e.preventDefault();
    const lineKey = removeBtn.getAttribute('data-cart-remove');
    updateCart(lineKey, 0);
    return;
  }

  // Alternative remove button selector (cart-remove-button elements)
  const cartRemoveBtn = e.target.closest('cart-remove-button');
  if (cartRemoveBtn) {
    e.preventDefault();
    const index = cartRemoveBtn.dataset.index;
    updateCart(index, 0);
    return;
  }

  // ➕/➖ Quantity adjustment
  const qtyBtn = e.target.closest('.quantity__button');
  if (qtyBtn) {
    e.preventDefault();
    const parent = qtyBtn.closest('[data-cart-item]');
    if (!parent) return;
    
    const key = parent.getAttribute('data-cart-item');
    const input = parent.querySelector('.quantity__input');
    if (!input) return;
    
    let val = parseInt(input.value, 10) || 0;
    
    if (qtyBtn.name === 'plus') {
      val++;
    } else if (qtyBtn.name === 'minus') {
      val = Math.max(0, val - 1); // Allow 0 to remove item
    }
    
    updateCart(key, val);
  }
});

// Handle quantity input changes
document.addEventListener('change', (e) => {
  if (e.target.matches('.quantity__input')) {
    const parent = e.target.closest('[data-cart-item]');
    if (!parent) return;
    
    const key = parent.getAttribute('data-cart-item');
    let quantity = parseInt(e.target.value, 10) || 0;
    
    // Ensure minimum quantity is 0 (to allow removal)
    quantity = Math.max(0, quantity);
    
    updateCart(key, quantity);
  }
});

// Checkout button functionality (unchanged)
document.addEventListener('DOMContentLoaded', function () {
  const checkoutBtn = document.querySelector('#CartDrawer-Checkout');

  if (checkoutBtn) {
    checkoutBtn.addEventListener('click', function (e) {
      e.preventDefault(); // voorkom directe doorgang

      // Verzamel alle aangekruiste woonopties
      const attributes = {};
      document.querySelectorAll('.cart__custom-woonoptie input[type="checkbox"]').forEach(input => {
        if (input.checked) {
          attributes[input.name.replace('attributes[', '').replace(']', '')] = input.value;
        }
      });

      // Voeg toe aan winkelwagen via Shopify AJAX API
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attributes })
      })
      .then(response => response.json())
      .then(data => {
        // Ga nu door naar checkout
        window.location.href = '/checkout';
      })
      .catch(error => {
        console.error('Woonopties opslaan mislukt:', error);
        window.location.href = '/checkout'; // toch doorgaan bij fout
      });
    });
  }
});

const tryOpenCartDrawer = () => {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer && typeof cartDrawer.open === 'function') {
    cartDrawer.open();
  } else {
    // probeer het opnieuw na 100ms
    setTimeout(tryOpenCartDrawer, 100);
  }
};

document.addEventListener('DOMContentLoaded', function () {
  if (window.location.pathname === '/pages/design-your-own-table' &&
      sessionStorage.getItem('openCartAfterReload') === 'true') {
    sessionStorage.removeItem('openCartAfterReload');
    tryOpenCartDrawer();
  }
});
